import { buildAddWidgetPrompt, harvestWidgets } from './reportBuild';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createReportChatTransport } from './reportChat';
import { lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useAuth0 } from '@auth0/auth0-react';
import { useChat } from '@ai-sdk/react';
import useCountryParam from '@/utils/hooks/useCountryParam';
import useSearchableIndustries from '@/utils/hooks/useSearchableIndustries';

/**
 * Turns a free-text request ("skincare overview in the US") into a report widget
 * config, reusing the same agent + harvest path as the full report build. It
 * fires on demand and clears the chat between adds, so each addWidget()
 * harvests exactly the widget(s) that request produced.
 * Calls onAdded(widgets) with the harvested configs (entity_index already
 * resolved to entity_id/route). Colocated with the Reports views.
 *
 * @returns {{ addWidget, isAdding, error, emptyResult }} — `emptyResult` is true
 * when the last add settled with no renderable widget (so the UI can prompt a
 * rephrase); it resets on the next addWidget call.
 */
const useAddWidget = (onAdded) => {
    const { getAccessTokenSilently } = useAuth0();
    const { currentCountryParam } = useCountryParam();

    const geoRef = useRef(currentCountryParam);
    geoRef.current = currentCountryParam;

    // No composer here, so widget adds always run on the auto model tier.
    const modelTierRef = useRef(null);

    const industries = useSearchableIndustries();
    const industriesRef = useRef(industries);
    industriesRef.current = industries;

    const transport = useMemo(
        () =>
            createReportChatTransport({
                getAccessTokenSilently,
                geoRef,
                modelTierRef,
                industriesRef,
            }),
        [getAccessTokenSilently],
    );

    const { messages, sendMessage, setMessages, status, error } = useChat({
        transport,
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    });

    const isAdding = status === 'streaming' || status === 'submitted';
    const [emptyResult, setEmptyResult] = useState(false);

    const onAddedRef = useRef(onAdded);
    onAddedRef.current = onAdded;
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    // True between a send and its settle, so the settle effect harvests exactly
    // the just-completed add and ignores unrelated status churn.
    const pendingRef = useRef(false);

    const addWidget = useCallback(
        (text) => {
            const prompt = text.trim();
            if (!prompt || pendingRef.current) return;
            pendingRef.current = true;
            setEmptyResult(false);
            // Each add is independent: clear the prior chat so harvest only sees
            // this request's tool calls.
            setMessages([]);
            sendMessage({ text: buildAddWidgetPrompt(prompt) });
        },
        [sendMessage, setMessages],
    );

    // Harvest on the adding→settled transition (excluding errors). An empty
    // harvest means the agent produced nothing renderable — flag it so the UI can
    // ask for a rephrase rather than silently doing nothing.
    const prevAddingRef = useRef(isAdding);
    useEffect(() => {
        const wasAdding = prevAddingRef.current;
        prevAddingRef.current = isAdding;
        if (!pendingRef.current || !(wasAdding && !isAdding)) return;
        pendingRef.current = false;
        if (status === 'error') return; // surfaced via `error`
        const widgets = harvestWidgets(messagesRef.current);
        if (widgets.length > 0) onAddedRef.current(widgets);
        else setEmptyResult(true);
    }, [isAdding, status]);

    return { addWidget, isAdding, error, emptyResult };
};

export default useAddWidget;
